/**
 * Cache Sync Manager
 *
 * Handles cross-tab cache synchronization using BroadcastChannel API.
 * Ensures all browser tabs maintain consistent cache state.
 */

import { log } from '../../utils/DebugLogger'

const CHANNEL_NAME = 'franchise-factory-cache-sync'

interface SyncMessage {
  type: 'invalidate' | 'invalidatePattern' | 'clear' | 'update'
  key?: string
  pattern?: string
  value?: unknown
  timestamp: number
}

interface CacheServiceLike {
  _invalidateLocal(key: string): Promise<void> | void
  _invalidatePatternLocal(pattern: string): Promise<number> | number
  _clearLocal(): Promise<void> | void
}

class CacheSyncManager {
  private channel: BroadcastChannel | null = null
  private cacheService: CacheServiceLike | null = null
  private isSupported: boolean = typeof BroadcastChannel !== 'undefined'

  init(cacheService: CacheServiceLike): void {
    this.cacheService = cacheService

    if (!this.isSupported) {
      log('CacheSyncManager', 'init', 'BroadcastChannel not supported, cross-tab sync disabled')
      return
    }

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME)
      this.channel.onmessage = this.handleMessage.bind(this)
      log('CacheSyncManager', 'init', 'Cross-tab sync initialized')
    } catch (error) {
      log('CacheSyncManager', 'init', 'Failed to create BroadcastChannel', error)
      this.isSupported = false
    }
  }

  handleMessage(event: MessageEvent<SyncMessage>): void {
    const { type, key, pattern, timestamp } = event.data

    log('CacheSyncManager', 'handleMessage', `Received: ${type}`, { key, pattern })

    if (timestamp && Date.now() - timestamp > 5000) {
      log('CacheSyncManager', 'handleMessage', 'Ignoring stale message')
      return
    }

    if (!this.cacheService) return

    switch (type) {
      case 'invalidate':
        if (key) this.cacheService._invalidateLocal(key)
        break

      case 'invalidatePattern':
        if (pattern) this.cacheService._invalidatePatternLocal(pattern)
        break

      case 'clear':
        this.cacheService._clearLocal()
        break

      case 'update':
        log('CacheSyncManager', 'handleMessage', `Update received for: ${key}`)
        break

      default:
        log('CacheSyncManager', 'handleMessage', `Unknown message type: ${type}`)
    }
  }

  broadcastInvalidate(key: string): void {
    this.broadcast({
      type: 'invalidate',
      key,
      timestamp: Date.now()
    })
  }

  broadcastInvalidatePattern(pattern: string): void {
    this.broadcast({
      type: 'invalidatePattern',
      pattern,
      timestamp: Date.now()
    })
  }

  broadcastClear(): void {
    this.broadcast({
      type: 'clear',
      timestamp: Date.now()
    })
  }

  broadcastUpdate(key: string, value: unknown = null): void {
    this.broadcast({
      type: 'update',
      key,
      value,
      timestamp: Date.now()
    })
  }

  broadcast(message: SyncMessage): void {
    if (!this.isSupported || !this.channel) {
      return
    }

    try {
      this.channel.postMessage(message)
      log('CacheSyncManager', 'broadcast', message.type, { key: message.key, pattern: message.pattern })
    } catch (error) {
      log('CacheSyncManager', 'broadcast', 'Failed to broadcast', error)
    }
  }

  close(): void {
    if (this.channel) {
      this.channel.close()
      this.channel = null
      log('CacheSyncManager', 'close', 'Channel closed')
    }
  }

  isSyncAvailable(): boolean {
    return this.isSupported && this.channel !== null
  }
}

export const cacheSyncManager = new CacheSyncManager()
export { CacheSyncManager }
